import { useState, type MouseEvent, type ReactNode } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  Button,
  Menu,
  MenuItem,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Link, useLocation, useNavigate } from 'react-router';
import { useAuth } from '../hooks/useAuth';
import { useNavItems } from '../context/NavItemsContext';
import { PAGE_PX } from '../constants/layout';
import logoUrl from '../assets/logo.svg';

// Responsive sidebar width — narrower on small screens, roomier on large displays.
const SIDEBAR_WIDTH = { xs: 190, md: 200, lg: 220, xl: 240 };

interface AppLayoutProps {
  children: ReactNode;
  /** Page title shown on the left of the top header bar. */
  title?: string;
  /** Optional descriptive subtitle shown under the title in the header. */
  subtitle?: string;
  /**
   * Horizontal padding for the header row. Defaults to PAGE_PX so the header lines
   * up with the standard page gutters; pages with a tighter content gutter (e.g. the
   * backoffice encounter workspace at px:4) can override to keep the title aligned.
   */
  headerPx?: number | string | Record<string, number>;
  /** When provided, a back link is rendered above the title. */
  onBack?: () => void;
  /** Label for the back link (e.g. 'Claims', 'Back to Claims'). Defaults to 'Back'. */
  backLabel?: string;
  /** Whether to render the primary-action CTA in the sidebar. Defaults to true (portal). */
  showPrimaryAction?: boolean;
  /** Label for the primary-action CTA button. Defaults to 'New' (portal). */
  primaryActionLabel?: string;
  /** Route the CTA navigates to when it has a single action. Defaults to '/submit'. */
  primaryActionPath?: string;
  /**
   * Menu options for the primary-action CTA. With more than one option the button
   * opens a menu; with one (or none) it navigates directly. Defaults to the
   * portal's New Claim / New GAP Request options.
   */
  primaryActionItems?: Array<{ label: string; path: string; icon?: ReactNode }>;
}

function getInitials(name: string | undefined): string {
  if (!name) return '--';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

export default function AppLayout({
  children,
  title,
  subtitle,
  headerPx = PAGE_PX,
  onBack,
  backLabel = 'Back',
  showPrimaryAction = true,
  primaryActionLabel = 'New',
  primaryActionPath = '/submit',
  primaryActionItems = [
    { label: 'New Claim', path: '/submit', icon: <AssignmentOutlinedIcon fontSize="small" /> },
    { label: 'New GAP Request', path: '/gap-request', icon: <DescriptionOutlinedIcon fontSize="small" /> },
  ],
}: AppLayoutProps) {
  const { user, isLoading, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const navItems = useNavItems();
  const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null);
  const profileOpen = Boolean(profileAnchor);
  const [primaryAnchor, setPrimaryAnchor] = useState<null | HTMLElement>(null);
  const primaryMenuOpen = Boolean(primaryAnchor);
  const hasPrimaryMenu = primaryActionItems.length > 1;
  const isActive = (base: string) => pathname === base || pathname.startsWith(`${base}/`);
  const goToPrimaryAction = (path: string) => {
    navigate(path, { state: { restart: Date.now() } });
  };
  const handlePrimaryActionClick = (e: MouseEvent<HTMLElement>) => {
    if (hasPrimaryMenu) {
      setPrimaryAnchor(e.currentTarget);
    } else {
      goToPrimaryAction(primaryActionItems[0]?.path ?? primaryActionPath);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Drawer
        variant="permanent"
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH,
            bgcolor: '#000000',
            borderRight: 'none',
            borderRadius: 0,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Logo */}
        <Box sx={{ px: 3, pt: 5, pb: 0 }}>
          <Box
            component="img"
            src={logoUrl}
            alt="Alteva"
            sx={{ width: 150, height: 'auto', display: 'block' }}
          />
        </Box>

        {/* Primary-action CTA — white button, black text */}
        {showPrimaryAction && (
          <Box sx={{ px: 3, mt: '25px', mb: '36px' }}>
            <Button
              variant="contained"
              fullWidth
              startIcon={<AddCircleOutlineIcon sx={{ fontSize: '23px !important' }} />}
              onClick={handlePrimaryActionClick}
              aria-haspopup={hasPrimaryMenu ? 'true' : undefined}
              aria-expanded={primaryMenuOpen ? 'true' : undefined}
              sx={{
                bgcolor: '#ffffff',
                color: '#000000',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: 15,
                borderRadius: 1,
                height: '48px',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#eeeeee', boxShadow: 'none' },
              }}
            >
              {primaryActionLabel}
            </Button>
            {hasPrimaryMenu && (
              <Menu
                anchorEl={primaryAnchor}
                open={primaryMenuOpen}
                onClose={() => setPrimaryAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{
                  paper: {
                    sx: {
                      // At least as wide as the button; grows to fit wider content.
                      minWidth: primaryAnchor ? primaryAnchor.clientWidth : undefined,
                      // 1px gap between the button and the menu.
                      mt: '1px',
                      // Match the page background rather than the default paper white.
                      bgcolor: 'background.default',
                    },
                  },
                }}
              >
                {primaryActionItems.map((item) => (
                  <MenuItem
                    key={item.path}
                    onClick={() => {
                      setPrimaryAnchor(null);
                      goToPrimaryAction(item.path);
                    }}
                  >
                    {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
                    <ListItemText>{item.label}</ListItemText>
                  </MenuItem>
                ))}
              </Menu>
            )}
          </Box>
        )}

        {/* Nav items — white text; selected = transparent white overlay */}
        <List sx={{ px: 3, '& > li + li': { mt: '12px' } }}>
          {navItems.map(({ to, label, icon: Icon }) => {
            const active = isActive(to);
            return (
              <ListItemButton
                key={to}
                component={Link}
                to={to}
                sx={{
                  borderRadius: 1,
                  minHeight: '46px',
                  color: '#ffffff',
                  bgcolor: active ? 'rgba(255,255,255,0.2)' : 'transparent',
                  '&:hover': {
                    bgcolor: active ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.08)',
                  },
                }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}>
                  <Icon sx={{ fontSize: 18 }} />
                </ListItemIcon>
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{ fontSize: 15, fontWeight: 500 }}
                />
              </ListItemButton>
            );
          })}
        </List>

        <Box sx={{ flex: 1 }} />
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          bgcolor: 'background.default',
          height: '100vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Top header — page title (left) + user profile (right) */}
        <Box
          sx={{
            flex: '0 0 auto',
            zIndex: (theme) => theme.zIndex.appBar,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
            px: headerPx,
            py: 3,
            pr: { xs: 4, md: 5, lg: 7, xl: 9 } ,
            minHeight: 76,
            boxSizing: 'border-box',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            {onBack && (
              <Box
                component="button"
                type="button"
                onClick={onBack}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mb: 0.5,
                  bgcolor: 'transparent',
                  border: 'none',
                  p: 0,
                  cursor: 'pointer',
                  color: 'text.secondary',
                  fontSize: 13,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 14 }} /> {backLabel}
              </Box>
            )}
            <Typography
              component="h2"
              sx={{
                fontSize: { xs: 24, md: 28 },
                fontWeight: 600,
                color: 'text.primary',
                lineHeight: 1.2,
                letterSpacing: '-0.5px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                m: 0,
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography sx={{ fontSize: 13, fontWeight: 400, color: 'text.secondary', mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
          </Box>

          <Box sx={{ flexShrink: 0 }}>
            <Box
              component="button"
              type="button"
              onClick={(e) => setProfileAnchor(e.currentTarget)}
              aria-label="Account menu"
              aria-haspopup="true"
              aria-expanded={profileOpen ? 'true' : undefined}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                bgcolor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                p: '4px',
                borderRadius: 1,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: 'primary.light',
                  color: 'text.primary',
                  fontSize: 14,
                  fontWeight: 500,
                  borderRadius: 1,
                }}
                variant="rounded"
              >
                {isLoading ? '--' : getInitials(user?.name)}
              </Avatar>
              <Box sx={{ minWidth: 0, textAlign: 'left' }}>
                <Typography sx={{ fontWeight: 500, fontSize: 14, color: 'text.primary', lineHeight: 1.3 }}>
                  {isLoading ? 'Loading...' : (user?.name ?? 'Unknown User')}
                </Typography>
                {user?.organizationName && (
                  <Typography sx={{ color: 'text.secondary', fontSize: 12, fontWeight: 400, lineHeight: 1.3 }}>
                    {user.organizationName}
                  </Typography>
                )}
              </Box>
              <KeyboardArrowDownIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            </Box>
            <Menu
              anchorEl={profileAnchor}
              open={profileOpen}
              onClose={() => setProfileAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem
                onClick={() => {
                  setProfileAnchor(null);
                  logout();
                }}
              >
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Sign out</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}
